// REFACTOR-SPLIT: ATTEST-DEPLOY-UNIT-1 R2 prep 2/3 — DealStatus, EngagementType, Deal,
// FinancialYear and EvidenceEntry now live in `@/api/sharedTypes` so
// api/client.ts (the one file every product's build includes) has no import
// specifier pointing into this QoE-owned file. Re-exported below so every
// existing `@/types/deal` import keeps working unchanged.
import type { Deal, DealStatus, EngagementType, EvidenceEntry, FinancialYear } from '@/api/sharedTypes'

export type { Deal, DealStatus, EngagementType, EvidenceEntry, FinancialYear }

export const ENGAGEMENT_TYPE_LABELS: Record<EngagementType, string> = {
  buy_side: 'Buy-side',
  sell_side: 'Sell-side',
}

/**
 * QOE-TRUTH-AND-JOURNEY-1 R16 (owner correction, 2026-08-23) — the buyer's
 * view of deal status, derived from the same DealStatus/FORWARD_STATUS_ORDER
 * spine the CPA-facing STATUS_LABELS maps use, but collapsed to three
 * buckets instead of six. A single function so the seller spine and the
 * buyer view can never drift apart — same reasoning as reusing
 * report_completeness instead of writing a second one.
 *
 * Why collapsed, not mirrored:
 *   - "In Analysis" as a distinct buyer-visible state is the seller's
 *     status leaking to a counterparty — it's negotiating information
 *     (how far along diligence is), not a fact the buyer is owed.
 *   - `withdrawn` is deliberately absent: it's off-spine and terminal, and
 *     the buyer token stops resolving (401) before this function ever runs
 *     — see apps/api/qoe_portal.py _resolve_buyer_token. Returning null
 *     here for a status this function should never receive is a defensive
 *     backstop, not the enforcement point; it must never silently fall
 *     through to a generic default.
 *
 * No `subject`/engagement_type param: the copy below ("In progress", "The
 * QoE report is ready to review.", "This engagement has closed.") is
 * already advisor-neutral, so there is nothing possessive for a caller to
 * pass sell_side- vs buy_side-aware text for. If a future status ever needs
 * "your advisor" language, that is the point to reintroduce an
 * engagement_type-aware param — don't restore this docstring's old shape
 * speculatively.
 */
export interface BuyerStatusView {
  label: string
  detail: string
}

export function buyerStatusView(status: DealStatus): BuyerStatusView | null {
  switch (status) {
    case 'setup':
    case 'seller_intake':
    case 'in_analysis':
      return { label: 'In progress', detail: 'Nothing to review yet.' }
    case 'report_ready':
    case 'delivered':
      return { label: 'Report available', detail: 'The QoE report is ready to review.' }
    case 'closed':
      return { label: 'Closed', detail: 'This engagement has closed.' }
    case 'withdrawn':
      // Should be unreachable — the buyer token stops resolving first.
      // No default bucket: an unhandled status here is a bug to surface,
      // not something to paper over with "In progress".
      return null
    default:
      return null
  }
}

export interface Finding {
  finding_id: string
  deal_id: string
  category: string
  label: string
  score: 'PASS' | 'REVIEW' | 'FLAG' | 'PENDING'
  narrative: string | null
  ai_draft: string | null
  approved_by: string | null
  approved_at: string | null
}

export interface NormalizationSession {
  deal_id: string
  financial_years: FinancialYear[]
  adjustments: EbitdaAdjustment[]
  working_capital: WorkingCapitalData | null
  revenue_quality: RevenueQualityData | null
  last_saved: string
}

export interface EbitdaAdjustment {
  adjustment_id: string
  category: string
  description: string
  amount: number
  year: number
  /** FIELDS-1 P2 — "Still Needed". A proposed add-back the analyst has written
   *  down but cannot yet support. Excluded from every calc by
   *  `financialMath.confirmedAdjustments` and by `core/financial_math.py`, so it
   *  documents the open item without inflating adjusted EBITDA or SDE. */
  is_pending?: boolean
}

export interface WorkingCapitalData {
  ar_0_30: number | null
  ar_31_60: number | null
  ar_61_90: number | null
  ar_over_90: number | null
  dso: number | null
  ap_0_30: number | null
  ap_31_60: number | null
  ap_61_90: number | null
  dpo: number | null
  inventory: number | null
  capex_prior_year: number | null
  capex_ttm: number | null
}

export interface RevenueQualityData {
  top_customers: CustomerConcentration[]
  recurring_pct: number | null
  cagr_3yr: number | null
  seasonality_index: number | null
}

export interface CustomerConcentration {
  customer_name: string
  revenue_pct: number
}
